import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight, 
  ArrowDownRight,
  Info,
  ExternalLink,
  Link as LinkIcon,
  Loader2
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StockItem } from "@/types/stock-item";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { useGLAccounts } from "@/hooks/use-gl-accounts";

interface StockItemAnalysisTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange: (item: StockItem) => void;
}

export function StockItemAnalysisTab({ item, isEditMode, onItemChange }: StockItemAnalysisTabProps) {
  // Check integration status
  const integrationStatus = useIntegrationStatus();
  const isIntegrated = integrationStatus.isAnyIntegrated;
  
  // Fetch GL accounts when integrated
  const { data: glAccounts, isLoading: isLoadingAccounts } = useGLAccounts(isIntegrated);
  
  // Generate mock movement data
  const totalMovements = Math.floor(Math.random() * 450) + 50; // 50-500
  const inboundMovements = Math.floor(totalMovements * (0.4 + Math.random() * 0.2)); // 40-60%
  const outboundMovements = Math.floor(totalMovements * (0.4 + Math.random() * 0.2)); // 40-60%
  const netMovement = inboundMovements - outboundMovements;

  // Get ABC classification details
  const getABCDetails = (classification: "A" | "B" | "C") => {
    switch (classification) {
      case "A":
        return {
          variant: "success" as const,
          label: "Class A",
          description: "High value - represents 80% of inventory value",
          bgColor: "bg-green-50 dark:bg-green-950/30",
          borderColor: "border-green-200 dark:border-green-800"
        };
      case "B":
        return {
          variant: "warning" as const,
          label: "Class B",
          description: "Medium value - represents 15% of inventory value",
          bgColor: "bg-yellow-50 dark:bg-yellow-950/30",
          borderColor: "border-yellow-200 dark:border-yellow-800"
        };
      case "C":
        return {
          variant: "secondary" as const,
          label: "Class C",
          description: "Low value - represents 5% of inventory value",
          bgColor: "bg-gray-50 dark:bg-gray-950/30",
          borderColor: "border-gray-200 dark:border-gray-800"
        };
    }
  };

  // Get stock turn rate indicator
  const getStockTurnIndicator = (rate: number) => {
    if (rate > 6.0) {
      return {
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-50 dark:bg-green-950/30",
        label: "Fast Moving",
        icon: TrendingUp,
        trend: "up"
      };
    } else if (rate >= 2.0) {
      return {
        color: "text-orange-600 dark:text-orange-400",
        bgColor: "bg-orange-50 dark:bg-orange-950/30",
        label: "Medium",
        icon: BarChart3,
        trend: "neutral"
      };
    } else {
      return {
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-950/30",
        label: "Slow Moving",
        icon: TrendingDown,
        trend: "down"
      };
    }
  };

  const abcDetails = getABCDetails(item.abcClassification);
  const stockTurnIndicator = getStockTurnIndicator(item.stockTurnRate);
  const StockTurnIcon = stockTurnIndicator.icon;

  const handleInputChange = (field: keyof StockItem, value: string) => {
    onItemChange({
      ...item,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      {/* Analysis Codes Section */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Analysis Codes</h3>
          {isIntegrated && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <LinkIcon className="h-3 w-3" />
                    ERP Integrated
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Connected to {integrationStatus.isFinancialsIntegrated ? "Financials" : "Dimensions"} module</p>
                  <p className="text-xs text-muted-foreground mt-1">GL accounts synced from ERP</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        {isLoadingAccounts && isIntegrated ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading GL accounts...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Purchase Analysis Code */}
            <div className="space-y-2">
              <Label htmlFor="purchaseAnalysisCode">Purchase Analysis Code</Label>
              {isIntegrated && glAccounts ? (
                <Select
                  value={item.purchaseAnalysisCode || ""}
                  onValueChange={(value) => handleInputChange("purchaseAnalysisCode", value)}
                  disabled={!isEditMode}
                >
                  <SelectTrigger id="purchaseAnalysisCode">
                    <SelectValue placeholder="Select GL account" />
                  </SelectTrigger>
                  <SelectContent>
                    {glAccounts
                      .filter((account) => account.isActive)
                      .map((account) => (
                        <SelectItem key={account.code} value={account.code}>
                          {account.code} - {account.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="purchaseAnalysisCode"
                  value={item.purchaseAnalysisCode || ""}
                  onChange={(e) => handleInputChange("purchaseAnalysisCode", e.target.value)}
                  disabled={!isEditMode}
                  maxLength={20}
                  placeholder="Enter code"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {isIntegrated ? "GL account for purchase transactions" : "Code for purchase reporting"}
              </p>
            </div>

            {/* Sales Analysis Code */}
            <div className="space-y-2">
              <Label htmlFor="salesAnalysisCode">Sales Analysis Code</Label>
              {isIntegrated && glAccounts ? (
                <Select
                  value={item.salesAnalysisCode || ""}
                  onValueChange={(value) => handleInputChange("salesAnalysisCode", value)}
                  disabled={!isEditMode}
                >
                  <SelectTrigger id="salesAnalysisCode">
                    <SelectValue placeholder="Select GL account" />
                  </SelectTrigger>
                  <SelectContent>
                    {glAccounts
                      .filter((account) => account.isActive)
                      .map((account) => (
                        <SelectItem key={account.code} value={account.code}>
                          {account.code} - {account.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="salesAnalysisCode"
                  value={item.salesAnalysisCode || ""}
                  onChange={(e) => handleInputChange("salesAnalysisCode", e.target.value)}
                  disabled={!isEditMode}
                  maxLength={20}
                  placeholder="Enter code"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {isIntegrated ? "GL account for sales transactions" : "Code for sales reporting"}
              </p>
            </div>

            {/* Movement In Analysis */}
            <div className="space-y-2">
              <Label htmlFor="movementInAnalysis">Movement In Analysis Code</Label>
              {isIntegrated && glAccounts ? (
                <Select
                  value={item.movementInAnalysis || ""}
                  onValueChange={(value) => handleInputChange("movementInAnalysis", value)}
                  disabled={!isEditMode}
                >
                  <SelectTrigger id="movementInAnalysis">
                    <SelectValue placeholder="Select GL account" />
                  </SelectTrigger>
                  <SelectContent>
                    {glAccounts
                      .filter((account) => account.isActive)
                      .map((account) => (
                        <SelectItem key={account.code} value={account.code}>
                          {account.code} - {account.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="movementInAnalysis"
                  value={item.movementInAnalysis || ""}
                  onChange={(e) => handleInputChange("movementInAnalysis", e.target.value)}
                  disabled={!isEditMode}
                  maxLength={20}
                  placeholder="Enter code"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {isIntegrated ? "GL account for inbound stock movements" : "Code for inbound movements"}
              </p>
            </div>

            {/* Movement Out Analysis */}
            <div className="space-y-2">
              <Label htmlFor="movementOutAnalysis">Movement Out Analysis Code</Label>
              {isIntegrated && glAccounts ? (
                <Select
                  value={item.movementOutAnalysis || ""}
                  onValueChange={(value) => handleInputChange("movementOutAnalysis", value)}
                  disabled={!isEditMode}
                >
                  <SelectTrigger id="movementOutAnalysis">
                    <SelectValue placeholder="Select GL account" />
                  </SelectTrigger>
                  <SelectContent>
                    {glAccounts
                      .filter((account) => account.isActive)
                      .map((account) => (
                        <SelectItem key={account.code} value={account.code}>
                          {account.code} - {account.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="movementOutAnalysis"
                  value={item.movementOutAnalysis || ""}
                  onChange={(e) => handleInputChange("movementOutAnalysis", e.target.value)}
                  disabled={!isEditMode}
                  maxLength={20}
                  placeholder="Enter code"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {isIntegrated ? "GL account for outbound stock movements" : "Code for outbound movements"}
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* ABC Classification Card */}
      <Card className={`p-6 border-2 ${abcDetails.borderColor} ${abcDetails.bgColor}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Badge variant={abcDetails.variant} className="text-lg px-4 py-1">
                {abcDetails.label}
              </Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Calculated based on inventory value contribution</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Label className="text-sm font-medium">ABC Classification</Label>
            <p className="text-sm text-muted-foreground mt-2">{abcDetails.description}</p>
            <p className="text-xs text-muted-foreground mt-1 italic">System calculated - read only</p>
          </div>
        </div>
      </Card>

      {/* Stock Performance Metrics */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Stock Performance Metrics</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Stock Turn Rate */}
          <div className={`p-4 rounded-lg border-2 ${stockTurnIndicator.bgColor}`}>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Stock Turn Rate</Label>
              <StockTurnIcon className={`h-5 w-5 ${stockTurnIndicator.color}`} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-4xl font-bold ${stockTurnIndicator.color}`}>
                {item.stockTurnRate.toFixed(1)}
              </span>
              <span className="text-sm text-muted-foreground">times/year</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={stockTurnIndicator.trend === "up" ? "success" : stockTurnIndicator.trend === "down" ? "destructive" : "secondary"} className="text-xs">
                {stockTurnIndicator.label}
              </Badge>
              {stockTurnIndicator.trend === "up" && <ArrowUpRight className="h-4 w-4 text-green-600" />}
              {stockTurnIndicator.trend === "down" && <ArrowDownRight className="h-4 w-4 text-red-600" />}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Number of times stock sold per year</p>
          </div>

          {/* Placeholder for future metric */}
          <div className="p-4 rounded-lg border-2 border-dashed border-muted bg-muted/20">
            <Label className="text-sm font-medium text-muted-foreground">Additional Metrics</Label>
            <p className="text-xs text-muted-foreground mt-2">More performance metrics coming soon</p>
          </div>
        </div>
      </Card>

      {/* Movement Analysis Summary */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Movement Analysis Summary</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Total Movements */}
          <div className="p-4 rounded-lg bg-muted/50">
            <Label className="text-sm font-medium text-muted-foreground">Total Movements This Year</Label>
            <div className="text-3xl font-bold mt-2">{totalMovements.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Year to date</p>
          </div>

          {/* Inbound Movements */}
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2">
              <Badge variant="success" className="text-xs">Inbound</Badge>
              <Label className="text-sm font-medium">Inbound Movements</Label>
            </div>
            <div className="text-3xl font-bold mt-2 text-green-600 dark:text-green-400">
              {inboundMovements.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((inboundMovements / totalMovements) * 100).toFixed(1)}% of total
            </p>
          </div>

          {/* Outbound Movements */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <Badge variant="default" className="text-xs">Outbound</Badge>
              <Label className="text-sm font-medium">Outbound Movements</Label>
            </div>
            <div className="text-3xl font-bold mt-2 text-blue-600 dark:text-blue-400">
              {outboundMovements.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((outboundMovements / totalMovements) * 100).toFixed(1)}% of total
            </p>
          </div>

          {/* Net Movement */}
          <div className={`p-4 rounded-lg border ${netMovement >= 0 ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
            <Label className="text-sm font-medium">Net Movement</Label>
            <div className={`text-3xl font-bold mt-2 ${netMovement >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {netMovement >= 0 ? '+' : ''}{netMovement.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Inbound - Outbound
            </p>
          </div>
        </div>
      </Card>

      {/* View Detailed Analysis Link */}
      <div className="flex justify-center">
        <Button variant="outline" className="gap-2" disabled>
          <ExternalLink className="h-4 w-4" />
          View Detailed Analysis
          <span className="text-xs text-muted-foreground ml-2">(Coming Soon)</span>
        </Button>
      </div>
    </div>
  );
}
